/** What a request says when it never reached the worker: offline, DNS, a dropped connection. */
export const NETWORK_ERROR_MESSAGE = "Couldn't reach Doopey's World. Check your connection and try again.";

/** A request error: the worker's `error` text as the message, with its status and body. */
export type RequestError = Error & {
  /** The HTTP status the worker answered with; unset when it never answered. */
  status?: number;
  data?: unknown;
  /** True when the request never got an answer (the message is NETWORK_ERROR_MESSAGE). */
  network?: boolean;
};

const newVoterToken = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
// Used when storage is blocked, so the visitor keeps one token for this visit.
let sessionVoterToken: string | null = null;

function getVoterToken() {
  try {
    let t = localStorage.getItem('vtoken');
    if (!t) {
      t = newVoterToken();
      localStorage.setItem('vtoken', t);
    }
    return t;
  } catch {
    return (sessionVoterToken ??= newVoterToken());
  }
}

interface RequestJsonOptions {
  includeVoterToken?: boolean;
}

async function parseResponseBody(res: Response): Promise<unknown> {
  const raw = await res.text();
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function getErrorMessage(data: unknown, fallback: string): string {
  const jsonMsg = data && typeof data === 'object' && 'error' in data ? String((data as { error: unknown }).error) : '';
  const textMsg = typeof data === 'string' ? data.trim() : '';
  return jsonMsg || textMsg || fallback;
}

/** Whether a request failed without an answer from the worker. */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if ((error as RequestError).network) return true;
  // A plain fetch() that failed: Chrome, Firefox and Safari each word it differently.
  return error instanceof TypeError && /failed to fetch|networkerror|load failed|network request failed/i.test(error.message);
}

/**
 * Whether a failed request is worth retrying: no answer at all, or a 5xx.
 * A 4xx (missing, expired, denied, invalid) won't change on a retry.
 */
export function isRetryableError(error: unknown): boolean {
  const status = (error as RequestError | null)?.status;
  return isNetworkError(error) || (typeof status === 'number' && status >= 500);
}

/**
 * Fetches JSON. A failed response throws the worker's `error` text with
 * `.status` and `.data`; a request that never got an answer throws
 * NETWORK_ERROR_MESSAGE with `.network`. An abort is rethrown as it is.
 */
export async function requestJson<T>(url: string, init?: RequestInit, options?: RequestJsonOptions): Promise<T> {
  const headers = new Headers(init?.headers ?? {});
  if (options?.includeVoterToken) headers.set('X-Voter-Token', getVoterToken());

  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw Object.assign(new Error(NETWORK_ERROR_MESSAGE), { network: true });
  }

  const data = await parseResponseBody(res);
  if (!res.ok) {
    throw Object.assign(new Error(getErrorMessage(data, `Request failed (${res.status})`)), {
      status: res.status,
      data,
    });
  }

  return data as T;
}

export async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  return requestJson<T>(url, init, { includeVoterToken: true });
}
